import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { useAtom } from "jotai";
import {
  notificationDialogAtom,
  dismissNotificationDialog,
  NotificationDialogType,
  NotificationDialogState,
} from "~/utils/notificationDialogUtils";
import "~/css/NotificationDialog.css";

/**
 * タイプごとのGoogle Fontsアイコン名マッピング
 */
const typeIconMap: Record<NotificationDialogType, string> = {
  success: "check",
  error: "error",
  info: "info",
  warning: "warning",
};

/**
 * 通知ダイアログコンポーネント
 * 画面右上に通知メッセージを表示するダイアログ
 * flashモード: 自動消滅、closableモード: 閉じるボタン付き
 * typeによりinfo(青系)/success(緑系)/warning(黄系)/error(赤系)の色分けとアイコン表示を行う
 * App.tsxなどルートに1箇所だけ配置する
 */
const NotificationDialog: React.FC = () => {
  const [dialogState] = useAtom(notificationDialogAtom);
  const [animationClass, setAnimationClass] = useState("");
  const [isVisible, setIsVisible] = useState(false);
  // フェードアウト中に表示内容を保持するためのref
  const lastStateRef = useRef<NotificationDialogState | null>(null);

  useEffect(() => {
    if (dialogState) {
      // 最新の表示内容を保持
      lastStateRef.current = dialogState;
      // 表示開始: フェードイン
      setIsVisible(true);
      const frameId = requestAnimationFrame(() => {
        setAnimationClass("notification-dialog-slide-in");
      });
      return () => cancelAnimationFrame(frameId);
    } else {
      // フェードアウトアニメーション後に非表示
      setAnimationClass("notification-dialog-slide-out");
      const timer = setTimeout(() => {
        setIsVisible(false);
        setAnimationClass("");
        lastStateRef.current = null;
      }, 150);
      return () => clearTimeout(timer);
    }
  }, [dialogState]);

  // フェードアウト中はlastStateRefの内容を表示する
  const displayState = dialogState || lastStateRef.current;

  if (!isVisible || !displayState) return null;

  const typeClass = `type-${displayState.type}`;

  const content = (
    <div className={`notification-dialog-container ${typeClass} ${animationClass}`} role="alert">
      <div className="notification-dialog-body">
        <span className="material-symbols-outlined notification-dialog-icon">
          {typeIconMap[displayState.type]}
        </span>
        <div className="notification-dialog-text">
          <span className="notification-dialog-message">{displayState.message}</span>
          {displayState.code !== undefined && (
            <span className="notification-dialog-code">{displayState.code}</span>
          )}
        </div>
        {displayState.mode === "closable" && (
          <button
            className="notification-dialog-close-button"
            onClick={dismissNotificationDialog}
            aria-label="close"
          >
            <span className="material-symbols-outlined">close</span>
          </button>
        )}
      </div>
    </div>
  );

  return createPortal(content, document.body);
};

export default NotificationDialog;
